(() => {
  const CONTROLLER = '__tsunPopupBanner';
  if (window[CONTROLLER]) return;
  window[CONTROLLER] = true;

  const DISMISS_PREFIX = 'popup_banner_dismissed_';
  const HIDE_DELAY_MS = 300;
  const MOBILE_MQ = '(max-width: 699px)';

  let activeBanner = null;
  let delayMet = false;
  let shown = false;
  let closing = false;
  let stopWaiting = () => {};

  function readStorage(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeStorage(key, value) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (e) {
      return false;
    }
  }

  function parseTime(raw) {
    if (!raw) return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    return value;
  }

  function isRecent(timestamp, windowMs) {
    if (timestamp == null) return false;
    return Date.now() - timestamp < windowMs;
  }

  function createTimestampHistory(resolveKey) {
    const memoryAt = new Map();
    return {
      isRecent(id, windowMs) {
        const stored = parseTime(readStorage(resolveKey(id)));
        return isRecent(memoryAt.get(id), windowMs) || isRecent(stored, windowMs);
      },
      mark(id) {
        const now = Date.now();
        memoryAt.set(id, now);
        writeStorage(resolveKey(id), String(now));
      },
    };
  }

  const dismissedAt = createTimestampHistory((bannerId) => DISMISS_PREFIX + bannerId);

  function isSuppressed(bannerId, suppressHours) {
    const hours = Number(suppressHours);
    if (!Number.isFinite(hours) || hours <= 0) return false;
    const windowMs = hours * 60 * 60 * 1000;
    return dismissedAt.isRecent(bannerId, windowMs);
  }

  function markDismissed(bannerId) {
    dismissedAt.mark(bannerId);
  }

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  function requiredScrollPercent(banner) {
    const raw = banner.dataset.scrollPercent;
    if (raw == null || String(raw).trim() === '') return 0;
    const value = Number(raw);
    if (!Number.isFinite(value) || value < 0) return 0;
    if (value > 100) return 100;
    return value;
  }

  function scrollProgress() {
    const scrolling = document.scrollingElement || document.documentElement;
    const max = scrolling.scrollHeight - scrolling.clientHeight;
    if (max <= 0) return 100;
    if (scrolling.scrollTop >= max - 1) return 100;
    return (scrolling.scrollTop / max) * 100;
  }

  function createBannerControls(onClose) {
    let activeBackdrop = null;
    let controlsBound = false;

    function removeBackdrop() {
      if (!activeBackdrop) return;
      activeBackdrop.remove();
      activeBackdrop = null;
    }

    function createBackdrop(banner) {
      if (activeBackdrop || isMobile() || !banner.classList.contains('popup-banner--center')) return;
      const backdrop = document.createElement('div');
      backdrop.className = 'popup-banner__backdrop';
      backdrop.setAttribute('data-popup-banner-backdrop', '');
      document.body.appendChild(backdrop);
      activeBackdrop = backdrop;
      window.requestAnimationFrame(() => {
        if (activeBackdrop === backdrop) backdrop.classList.add('popup-banner__backdrop--visible');
      });
      backdrop.addEventListener('click', () => onClose(banner));
    }

    function hideBackdrop() {
      if (activeBackdrop) activeBackdrop.classList.remove('popup-banner__backdrop--visible');
    }

    function sync() {
      if (!shown || !activeBanner) return;
      if (isMobile() || !activeBanner.classList.contains('popup-banner--center')) {
        removeBackdrop();
        return;
      }
      if (closing || activeBanner.hidden) return;
      createBackdrop(activeBanner);
    }

    function bind() {
      if (controlsBound) return;
      controlsBound = true;
      const mobileMq = window.matchMedia(MOBILE_MQ);
      const onChange = () => sync();
      if (typeof mobileMq.addEventListener === 'function') {
        mobileMq.addEventListener('change', onChange);
      } else if (typeof mobileMq.addListener === 'function') {
        mobileMq.addListener(onChange);
      }
      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !activeBanner || closing) return;
        if (!activeBanner.classList.contains('popup-banner--visible')) return;
        onClose(activeBanner);
      });
    }

    function bindCloseButton(banner) {
      const closeBtn = banner.querySelector('[data-popup-banner-close]');
      if (closeBtn) closeBtn.addEventListener('click', () => onClose(banner));
    }

    return { createBackdrop, removeBackdrop, hideBackdrop, bind, bindCloseButton };
  }

  function startWaiting(banner, delayMs) {
    let onReady = () => tryShow(banner);
    let delayTimer = null;
    window.addEventListener('scroll', onReady, { passive: true });
    window.addEventListener('resize', onReady, { passive: true });
    document.addEventListener('visibilitychange', onReady);
    delayTimer = window.setTimeout(() => {
      delayTimer = null;
      delayMet = true;
      tryShow(banner);
    }, delayMs);

    stopWaiting = () => {
      if (onReady) {
        window.removeEventListener('scroll', onReady);
        window.removeEventListener('resize', onReady);
        document.removeEventListener('visibilitychange', onReady);
        onReady = null;
      }
      if (delayTimer != null) {
        window.clearTimeout(delayTimer);
        delayTimer = null;
      }
    };
  }

  const bannerControls = createBannerControls(closeBanner);

  function showBanner(banner) {
    if (shown) return;
    shown = true;
    activeBanner = banner;
    stopWaiting();
    banner.hidden = false;
    bannerControls.createBackdrop(banner);
    bannerControls.bind();
    window.requestAnimationFrame(() => {
      if (activeBanner === banner) {
        banner.classList.add('popup-banner--visible');
        window.tsunShopifyAffiliate?.shown(banner);
      }
    });
    bannerControls.bindCloseButton(banner);
  }

  function closeBanner(banner) {
    if (!banner || closing) return;
    closing = true;
    window.tsunShopifyAffiliate?.hidden(banner);
    banner.classList.remove('popup-banner--visible');
    bannerControls.hideBackdrop();
    markDismissed(banner.dataset.bannerId);
    window.setTimeout(() => {
      banner.hidden = true;
      bannerControls.removeBackdrop();
    }, HIDE_DELAY_MS);
  }

  function tryShow(banner) {
    if (shown || closing) return;
    if (document.visibilityState !== 'visible') return;
    if (isSuppressed(banner.dataset.bannerId, banner.dataset.suppressHours)) {
      stopWaiting();
      return;
    }
    if (!delayMet) return;
    if (scrollProgress() < requiredScrollPercent(banner)) return;
    showBanner(banner);
  }

  function init() {
    const banners = Array.from(document.querySelectorAll('[data-popup-banner]'));
    const banner = banners.find((el) => !isSuppressed(el.dataset.bannerId, el.dataset.suppressHours));
    if (!banner) return;
    if (window.tsunShopifyAffiliate?.preparePopup(banner) === false) return;

    const delaySeconds = Number(banner.dataset.delay);
    const delayMs = Number.isFinite(delaySeconds) && delaySeconds > 0 ? delaySeconds * 1000 : 0;

    startWaiting(banner, delayMs);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
